import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import useUploadTramitesSAT from '../hooks/useUploadTramitesSAT';
import FileUploadBox from './FileUploadBox';
import SubmitSuccessScreen from './SubmitSuccessScreen';
import LoadingScreen from './LoadingScreen';
import ErrorScreen from './ErrorScreen';
import SolicitudInfoCard from './SolicitudInfoCard';
import { UPLOAD_CONFIG } from '../constants/fileStatus';

/**
 * Componente principal para carga de trámites SAT
 * Permite cargar documentación requerida para un trámite específico
 * Utiliza token de URL para obtener datos del trámite
 *
 * @param {Object} props - Props del componente
 * @param {Object} props.message - Estado de mensajes { open, message, severity }
 * @param {Function} props.setMessage - Función para actualizar mensajes
 */
const UploadTramitesSAT = ({ message, setMessage }) => {
  const {
    // Estado
    tramiteData,
    isLoading,
    error,
    documentacionFiles,
    isSubmitting,

    // React Hook Form
    control,
    handleSubmit,
    errors,

    // Métodos
    addFiles,
    removeFile,
    retryFile,
    restoreFile,
    onSubmit,

    canSubmit,
    isSuccess,
    submissionResponse,
    sessionExpired,
  } = useUploadTramitesSAT({ setMessage });

  const { afiliado, tipoCobertura, solicitudCobertura, documentacionTipoCobertura } =
    tramiteData || {};

  // Si la sesión expiró, mostrar pantalla de error
  if (sessionExpired) {
    return <ErrorScreen error={sessionExpired} />;
  }

  // Si el trámite fue enviado exitosamente, mostrar pantalla de éxito
  if (isSuccess) {
    return (
      <SubmitSuccessScreen
        idSolicitudCobertura={solicitudCobertura?.idSolicitudCoberturaFormateado || 0}
        onReset={() => window.location.reload()}
      />
    );
  }

  /**
   * Maneja el cambio de archivos para un tipo de documentación
   */
  const handleFilesChange = (idDocumentacion, newFiles) => {
    const currentFiles = documentacionFiles[idDocumentacion] || [];

    if (newFiles.length < currentFiles.length) {
      // Se eliminó un archivo
      const removedIndex = currentFiles.findIndex((fileMetadata, index) => {
        const newFile = newFiles[index];
        if (!newFile) return true;
        const currentFile = fileMetadata.file || fileMetadata;
        return currentFile !== (newFile.file || newFile);
      });
      if (removedIndex !== -1) {
        removeFile(idDocumentacion, removedIndex);
      }
    } else if (newFiles.length > currentFiles.length) {
      // Se agregaron archivos nuevos
      const addedFiles = newFiles.slice(currentFiles.length).map((item) => item.file || item); // Extraer File si viene en objeto
      addFiles(idDocumentacion, addedFiles);
    }
  };

  /**
   * Maneja el reintento de subida de un archivo
   */
  const handleRetryFile = (idDocumentacion) => (fileIndex) => {
    retryFile(idDocumentacion, fileIndex);
  };

  /**
   * Maneja la restauración de un archivo eliminado
   */
  const handleRestoreFile = (idDocumentacion) => (fileIndex) => {
    restoreFile(idDocumentacion, fileIndex);
  };

  // Renderizar estado de carga
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Renderizar estado de error
  if (error || !tramiteData) {
    return <ErrorScreen error={error} onRetry={() => window.location.reload()} />;
  }

  return (
    <Container disableGutters maxWidth='md' sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant='h4'
            component='h1'
            fontWeight='700'
            gutterBottom
            sx={{
              fontSize: { xs: '1.75rem', sm: '2.125rem' },
              textAlign: { xs: 'center', sm: 'left' },
            }}
          >
            Carga de Documentación SAT
          </Typography>
        </Box>

        {/* Formulario */}
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack spacing={3}>
            <SolicitudInfoCard
              numeroSolicitud={solicitudCobertura.idSolicitudCoberturaFormateado}
              afiliado={`(${afiliado.numeroDocumentoString}) ${afiliado.apellido}, ${afiliado.nombre}`}
              tramite={tipoCobertura.descripcion}
            />

            {/* Documentación a Adjuntar */}
            <Box>
              <Typography variant='h6' fontWeight='600' gutterBottom>
                Documentación a Adjuntar
              </Typography>

              {/* Mensaje de información */}
              <Alert severity='info' sx={{ borderRadius: 2, mb: 3 }}>
                <Typography variant='body2' gutterBottom>
                  <strong>Adjuntá la documentación</strong> para completar correctamente tu trámite.
                </Typography>
                <Typography
                  variant='body2'
                  gutterBottom
                  color='text.secondary'
                  fontSize={'0.775rem'}
                >
                  Los campos marcados con <strong style={{ color: 'red' }}>*</strong> son
                  requeridos, pero no impiden el envío del formulario.
                </Typography>
                <Typography
                  variant='body2'
                  gutterBottom
                  color='text.secondary'
                  fontSize={'0.775rem'}
                  fontWeight={'600'}
                >
                  Formatos permitidos PDF, JPG, JPEG · Hasta {UPLOAD_CONFIG.MAX_FILES_PER_DOC}{' '}
                  archivos · Máx. {UPLOAD_CONFIG.MAX_FILE_SIZE_MB}MB por archivo.
                </Typography>
              </Alert>

              {/* <Divider sx={{ mb: 4 }} /> */}

              <Stack spacing={3}>
                {documentacionTipoCobertura
                  ?.filter((doc) => doc.activo)
                  .map((doc) => (
                    <FileUploadBox
                      key={doc.idDocumentacion}
                      label={doc.documentacion}
                      required={doc.requerido}
                      files={documentacionFiles[doc.idDocumentacion] || []}
                      onFilesChange={(newFiles) => handleFilesChange(doc.idDocumentacion, newFiles)}
                      onRetry={handleRetryFile(doc.idDocumentacion)}
                      onRestore={handleRestoreFile(doc.idDocumentacion)}
                      maxFiles={UPLOAD_CONFIG.MAX_FILES_PER_DOC}
                      maxSizeMB={UPLOAD_CONFIG.MAX_FILE_SIZE_MB}
                      setMessage={setMessage}
                    />
                  ))}
              </Stack>
            </Box>

            {/* Mensaje de información */}
            {/* <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Nota importante:</strong> Asegúrese de adjuntar todos los documentos requeridos antes de enviar el formulario.
              </Typography>
            </Alert> */}

            {/* Botón Submit */}
            <Box sx={{ pt: 2 }}>
              <Button
                type='submit'
                variant='contained'
                size='large'
                fullWidth
                disabled={isSubmitting || !canSubmit().can}
                startIcon={
                  isSubmitting ? <CircularProgress size={20} color='inherit' /> : <SendIcon />
                }
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 4,
                  },
                }}
              >
                {isSubmitting ? 'Enviando trámite...' : 'Enviar Trámite'}
              </Button>

              {/* Mensaje de ayuda si no se puede enviar */}
              {!canSubmit().can && (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ mt: 1, display: 'block', textAlign: 'center' }}
                >
                  {canSubmit().reason}
                </Typography>
              )}
            </Box>
          </Stack>
        </form>
      </Paper>

      {/* Footer informativo */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant='caption' color='text.secondary'>
          ¿Tiene problemas con la carga? Verifique que los archivos cumplan con los requisitos de
          formato y tamaño.
        </Typography>
      </Box>
    </Container>
  );
};

export default UploadTramitesSAT;
